import React, { useState, useEffect } from 'react';
import { Dish, Extra } from '../types';
import { useData } from '../context/DataContext';
import { CameraIcon, FireIcon } from './icons';

interface DishFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (dish: Omit<Dish, 'id'> | Dish, category: string) => void;
  dishToEdit?: Dish | null;
  initialCategory: string;
  isSaving?: boolean;
}

const ALLOWED_CATEGORIES = [
  'Burgers', 'Pizzas', 'Plats', 'Desserts',
  'Salades', 'Pasta', 'Poissons', 'Traditionnel',
  'Mexicain', 'Entrée chaude', 'Sandwichs', 'Tacos', 'Crisps'
];

const DishFormModal: React.FC<DishFormModalProps> = ({ isOpen, onClose, onSave, dishToEdit, initialCategory, isSaving }) => {
  const { uploadImage } = useData();
  const [dish, setDish] = useState<{
    name: string,
    description: string,
    price: number,
    ingredients: string,
    image: string,
    discount: number,
    is_trending?: boolean,
    is_available?: boolean,
    extras: Extra[]
  }>({
    name: '',
    description: '',
    price: 0,
    ingredients: '',
    image: '',
    discount: 0,
    is_trending: false,
    is_available: true,
    extras: []
  });
  const [category, setCategory] = useState(initialCategory);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (dishToEdit) {
      setDish({
        ...dishToEdit,
        ingredients: dishToEdit.ingredients.join(', '),
        discount: dishToEdit.discount || 0,
        is_trending: dishToEdit.is_trending || false,
        is_available: dishToEdit.is_available ?? true,
        extras: dishToEdit.extras || []
      });
      setCategory(initialCategory);
    } else {
      setDish({
        name: '',
        description: '',
        price: 0,
        ingredients: '',
        image: '',
        discount: 0,
        is_trending: false,
        is_available: true,
        extras: []
      });
      setCategory(initialCategory);
    }
  }, [dishToEdit, isOpen, initialCategory]);

  const addExtra = () => {
    const newExtra: Extra = {
      id: Math.random().toString(36).substr(2, 9),
      name: '',
      price: 0
    };
    setDish(prev => ({ ...prev, extras: [...prev.extras, newExtra] }));
  };

  const removeExtra = (id: string) => {
    setDish(prev => ({ ...prev, extras: prev.extras.filter(e => e.id !== id) }));
  };

  const handleExtraChange = (id: string, field: keyof Extra, value: string | number) => {
    setDish(prev => ({
      ...prev,
      extras: prev.extras.map(e => e.id === id ? { ...e, [field]: value } : e)
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'category') {
      setCategory(value);
    } else {
      const val = (name === 'price' || name === 'discount') ? parseFloat(value) || 0 : value;
      setDish(prev => ({ ...prev, [name]: val }));
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const inputElement = e.target;

    try {
      setIsUploading(true);
      const url = await uploadImage(file);
      setDish(prev => ({ ...prev, image: url }));
    } catch (error: any) {
      alert(error.message || "Erreur lors de l'upload.");
    } finally {
      setIsUploading(false);
      inputElement.value = '';
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const dishData = { ...dish, ingredients: dish.ingredients.split(',').map(i => i.trim()) };
    onSave(dishToEdit ? { ...dishData, id: dishToEdit.id } : dishData, category);
  };

  if (!isOpen) return null;

  const finalPrice = Math.max(0, dish.price * (1 - (dish.discount || 0) / 100));

  return (
    <div className="fixed inset-0 bg-white md:bg-black/60 z-[10001] flex justify-center items-center overflow-hidden">
      <div className="bg-white rounded-none md:rounded-2xl shadow-2xl w-full h-full md:h-auto md:max-h-[85vh] md:max-w-4xl flex flex-col relative">

        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold text-gray-800">{dishToEdit ? 'Modifier le Plat' : 'Nouveau Plat'}</h2>

            {/* Availability Toggle */}
            <button
              type="button"
              onClick={() => setDish(prev => ({ ...prev, is_available: !prev.is_available }))}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all border-2 ${dish.is_available
                ? 'bg-green-50 border-green-200 text-green-700'
                : 'bg-gray-50 border-gray-200 text-gray-500'
                }`}
            >
              <div className={`w-2 h-2 rounded-full ${dish.is_available ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
              <span className="text-xs font-black uppercase tracking-wider">
                {dish.is_available ? 'En Ligne' : 'Hors Ligne'}
              </span>
            </button>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto p-4 md:p-8 flex-1 bg-white">
          <form id="dish-form" onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12">

            {/* Left Column: Details */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Nom du plat</label>
                <input
                  name="name"
                  value={dish.name}
                  onChange={handleChange}
                  placeholder="Ex: Burger Deluxe"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all outline-none"
                  required
                  disabled={isSaving}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Catégorie</label>
                <select
                  name="category"
                  value={category}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 outline-none bg-white"
                  disabled={isSaving}
                >
                  {ALLOWED_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                <textarea
                  name="description"
                  value={dish.description}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Décrivez ce plat délicieux..."
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 outline-none"
                  required
                  disabled={isSaving}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Ingrédients <span className="text-gray-400 font-normal">(séparés par virgule)</span></label>
                <input
                  name="ingredients"
                  value={dish.ingredients}
                  onChange={handleChange}
                  placeholder="Tomate, Oignon, Fromage..."
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 outline-none"
                  required
                  disabled={isSaving}
                />
              </div>
            </div>

            {/* Right Column: Visuals & Price */}
            <div className="space-y-6">

              {/* Image Uploader */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Photo du plat</label>
                <div className="relative w-full aspect-video rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 transition-colors group overflow-hidden">
                  {dish.image ? (
                    <>
                      <img src={dish.image} alt="Dish Preview" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <p className="text-white font-medium flex items-center"><CameraIcon className="w-5 h-5 mr-2" /> Changer la photo</p>
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                      <CameraIcon className="w-10 h-10 mb-2" />
                      <span className="text-sm">Cliquez pour ajouter une photo</span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    onChange={handleImageUpload}
                    disabled={isSaving || isUploading}
                  />
                  {isUploading && (
                    <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                    </div>
                  )}
                </div>
                <input
                  name="image"
                  value={dish.image}
                  onChange={handleChange}
                  placeholder="Ou coller une URL..."
                  className="w-full mt-2 px-3 py-2 text-sm rounded border border-gray-200 focus:border-red-500 outline-none"
                  disabled={isSaving || isUploading}
                />
              </div>

              {/* Extras Section */}
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden group/extras">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">✨</span>
                    <h4 className="font-bold text-gray-800">Extras & Suppléments</h4>
                  </div>
                  <button
                    type="button"
                    onClick={addExtra}
                    className="text-xs font-bold bg-green-600 text-white px-3 py-1.5 rounded-full hover:bg-green-700 transition-colors shadow-sm"
                  >
                    + Ajouter
                  </button>
                </div>

                <div className="space-y-3">
                  {dish.extras.length > 0 ? (
                    dish.extras.map((extra) => (
                      <div key={extra.id} className="flex gap-2 items-center animate-in fade-in slide-in-from-left-2 duration-200">
                        <div className="flex-grow">
                          <input
                            placeholder="Nom (ex: Supplément Fromage)"
                            value={extra.name}
                            onChange={(e) => handleExtraChange(extra.id, 'name', e.target.value)}
                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-red-500 outline-none transition-all"
                            disabled={isSaving}
                          />
                        </div>
                        <div className="w-24">
                          <input
                            type="number"
                            placeholder="Prix (DA)"
                            value={extra.price}
                            onChange={(e) => handleExtraChange(extra.id, 'price', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-red-500 outline-none transition-all font-bold text-green-600"
                            disabled={isSaving}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeExtra(extra.id)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                          disabled={isSaving}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="py-8 text-center border-2 border-dashed border-gray-100 rounded-xl">
                      <p className="text-gray-400 text-xs italic">Aucun supplément configuré pour ce plat.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Trending Toggle */}
              <div
                onClick={() => !isSaving && setDish(prev => ({ ...prev, is_trending: !prev.is_trending }))}
                className={`cursor-pointer p-4 rounded-xl border-2 transition-all flex items-center justify-between ${dish.is_trending ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-white hover:border-orange-200'}`}
              >
                <div className="flex items-center">
                  <div className={`p-2 rounded-full mr-3 ${dish.is_trending ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-400'}`}>
                    <FireIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className={`font-semibold ${dish.is_trending ? 'text-orange-900' : 'text-gray-700'}`}>Mettre en avant</h4>
                    <p className="text-xs text-gray-500">Affiche ce plat dans les tendances 🔥</p>
                  </div>
                </div>
                <div className={`w-6 h-6 rounded-full border flex items-center justify-center ${dish.is_trending ? 'bg-orange-500 border-orange-500' : 'border-gray-300'}`}>
                  {dish.is_trending && <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                </div>
              </div>

              {/* Pricing Section - MOVED TO BOTTOM */}
              <div className="bg-gray-50 p-4 rounded-xl space-y-4 border border-gray-100">
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Prix de base (DA)</label>
                    <input
                      name="price"
                      type="number"
                      value={dish.price}
                      onChange={handleChange}
                      className="w-full px-3 py-2 rounded border border-gray-300 focus:ring-2 focus:ring-red-500 outline-none font-medium text-lg"
                      required
                      disabled={isSaving}
                    />
                  </div>
                  <div className="w-1/3">
                    <label className="block text-xs font-bold text-red-500 uppercase mb-1">Promo %</label>
                    <input
                      name="discount"
                      type="number"
                      min="0"
                      max="100"
                      value={dish.discount}
                      onChange={handleChange}
                      className="w-full px-3 py-2 rounded border border-red-200 bg-red-50 text-red-600 focus:ring-2 focus:ring-red-500 outline-none font-bold text-lg"
                      disabled={isSaving}
                    />
                  </div>
                </div>

                {/* Final Price Preview */}
                <div className="flex justify-between items-center py-2 border-t border-gray-200 mt-2">
                  <span className="text-sm font-bold text-gray-600 tracking-tight">Estimation Finale :</span>
                  <div className="flex items-center gap-3">
                    {dish.discount > 0 && (
                      <div className="bg-gray-50 px-2.5 py-1 rounded-lg border border-gray-100 flex items-center gap-1.5 shadow-sm">
                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-tighter">Standard</span>
                        <span className="text-sm font-black text-red-600">
                          {dish.price.toLocaleString()} DA
                        </span>
                      </div>
                    )}
                    <span className="text-2xl font-black text-green-600 tabular-nums">
                      {finalPrice.toLocaleString()} DA
                    </span>
                  </div>
                </div>
              </div>

            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div className="p-4 md:p-6 border-t border-gray-100 bg-gray-50 flex justify-end space-x-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 md:flex-none px-6 py-3.5 rounded-xl border border-gray-300 text-gray-700 font-bold hover:bg-gray-100 transition-colors"
            disabled={isSaving || isUploading}
          >
            Annuler
          </button>
          <button
            type="submit"
            form="dish-form"
            className="flex-1 md:flex-none px-10 py-3.5 rounded-xl bg-red-600 text-white font-black hover:bg-red-700 shadow-xl shadow-red-100 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            disabled={isSaving || isUploading}
          >
            {isSaving ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              'Enregistrer'
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default DishFormModal;